import { useAuthStore } from "~/stores/authStore";
import logger from "~/utils/logger";

/**
 * Resilient WebSocket client with automatic token refresh and reconnection.
 *
 * Features:
 * - Token refresh on server "token expired/invalid" messages
 * - Exponential backoff with jitter (unlimited retries)
 * - Automatic reconnection on unexpected disconnects
 * - Circuit breaker for auth failures to prevent infinite loops
 *
 * Usage:
 *   const client = new WebSocketClient(url, { subscribeTo, projectId, token }, {
 *     tokenProvider: () => getStoredAccessToken(),
 *     onTokenExpired: async () => { await refreshToken(); }
 *   });
 *   client.connect();
 */

const STALE_CONNECTION_TIMEOUT_MS = 5 * 60_000; // 5 minutes
const HEARTBEAT_CHECK_INTERVAL_MS = 30_000; // 30 seconds
const MAX_CONSECUTIVE_AUTH_FAILURES = 3;
const BACKOFF_DEFAULTS = {
  baseMs: 1000,
  factor: 2,
  maxMs: 30_000,
  jitter: 0.2,
};

export type UpdateData = any;

type SubscriptionOptions = {
  subscribeTo:
    | "document-update-log"
    | "folder-update-log"
    | "term-definition-log"
    | "chat-message"
    | "volume-creation"
    | "presence";
  chatId?: string;
  token: string;
  projectId: string;
};

export type WebSocketClientRuntimeOptions = {
  tokenProvider?: () => string | null;
  onTokenExpired?: () => Promise<void> | void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
};

export type ChatMessageRequest = {
  messageType: "chat-message";
  action: "send-message";
  chatId: string;
  projectId: string;
  organizationId: string;
  message: string;
  token: string;
};

export type ChatMessageResponse = {
  messageType: "chat-message";
  action: "message-response" | "message-error";
  chatId: string;
  response?: {
    id: string;
    role: "assistant";
    content: string;
    timestamp: number;
  };
  error?: string;
};

export type VolumeCreationRequest = {
  messageType: "volume-creation";
  projectId: string;
  organizationId: string;
  name: string;
  documentIds: string[];
  token: string;
};

export type VolumeCreationResponse = {
  messageType: "volume-creation";
  action: "message-response" | "message-error";
  response?: {
    id: string;
    fileCount: number;
  };
  error?: string;
};
type EventMessageData = {
  data: SubscriptionOptions;
  updates: UpdateData[];
};
type ListenerCallback = (updates: UpdateData[]) => void;
type ErrorListenerCallback = (error: Event) => void;
type ChatMessageListenerCallback = (response: ChatMessageResponse) => void;
type VolumeCreationListenerCallback = (
  response: VolumeCreationResponse
) => void;

export class WebSocketClient {
  private url: string;
  private options: SubscriptionOptions;
  private socket: WebSocket | null;
  private listeners: ListenerCallback[];
  private reconnectAttempts: number = 0;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private lastMessageAt: number = 0;
  private errorListeners: ErrorListenerCallback[];
  private chatListeners: ChatMessageListenerCallback[];
  private volumeListeners: VolumeCreationListenerCallback[];
  private runtimeOptions: WebSocketClientRuntimeOptions;
  private isDestroyed: boolean = false;
  private intentionalClose: boolean = false;
  private consecutiveAuthFailures: number = 0;

  constructor(
    url: string,
    options: SubscriptionOptions,
    runtimeOptions?: WebSocketClientRuntimeOptions
  ) {
    this.url = url;
    this.options = options;
    this.socket = null;
    this.listeners = [];
    this.errorListeners = [];
    this.chatListeners = [];
    this.volumeListeners = [];
    this.runtimeOptions = {
      tokenProvider: runtimeOptions?.tokenProvider,
      onTokenExpired: runtimeOptions?.onTokenExpired,
      onOpen: runtimeOptions?.onOpen,
      onClose: runtimeOptions?.onClose,
    };
  }

  private clearTimers(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private calculateBackoffDelay(): number {
    const { baseMs, factor, maxMs, jitter } = BACKOFF_DEFAULTS;
    const exponentialDelay = Math.min(
      maxMs,
      baseMs * Math.pow(factor, this.reconnectAttempts)
    );
    const jitterAmount = exponentialDelay * jitter * (Math.random() * 2 - 1);
    return Math.max(0, exponentialDelay + jitterAmount);
  }

  /**
   * Attempts reconnection with exponential backoff and jitter.
   * Unlimited retries; resets attempt counter after successful connection.
   * Stops reconnecting if auth is expired.
   */
  private attemptReconnect(): void {
    const { isAuthExpired } = useAuthStore.getState();
    if (this.isDestroyed || this.reconnectTimer || isAuthExpired) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.calculateBackoffDelay();

    logger.info(
      `WebSocket reconnecting (attempt ${this.reconnectAttempts}, delay ${Math.round(delay)}ms)`
    );

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private getCurrentToken(): string {
    return this.runtimeOptions.tokenProvider?.() || this.options.token;
  }

  /**
   * Schedules stale connection monitoring.
   * Checks every HEARTBEAT_CHECK_INTERVAL_MS; only reconnects if connection is not OPEN
   * and no frames received for STALE_CONNECTION_TIMEOUT_MS.
   */
  private scheduleHeartbeat(): void {
    this.clearTimers();

    this.heartbeatTimer = window.setInterval(() => {
      if (this.isDestroyed) {
        return;
      }

      const idleTime = Date.now() - this.lastMessageAt;
      if (idleTime > STALE_CONNECTION_TIMEOUT_MS) {
        if (this.socket?.readyState !== WebSocket.OPEN) {
          logger.info(
            `WebSocket stale for ${Math.round(idleTime / 1000)}s, reconnecting`
          );
          this.attemptReconnect();
        }
      }
    }, HEARTBEAT_CHECK_INTERVAL_MS);
  }

  /**
   * Handles token expiration by triggering onTokenExpired callback and reconnecting.
   * Implements circuit breaker to prevent infinite auth failure loops.
   */
  private async handleTokenExpired(): Promise<void> {
    this.consecutiveAuthFailures++;

    logger.info(
      `WebSocket token expired, refreshing (auth failure ${this.consecutiveAuthFailures}/${MAX_CONSECUTIVE_AUTH_FAILURES})`
    );

    if (this.consecutiveAuthFailures >= MAX_CONSECUTIVE_AUTH_FAILURES) {
      logger.error(
        "WebSocket max consecutive auth failures reached, stopping reconnection"
      );
      const { setAuthExpired } = useAuthStore.getState();
      setAuthExpired(true);
      this.intentionalClose = true;
      this.disconnect();
      return;
    }

    try {
      await this.runtimeOptions.onTokenExpired?.();

      const newToken = this.getCurrentToken();
      if (newToken && newToken !== this.options.token) {
        this.options.token = newToken;
        logger.info("WebSocket token refreshed, reconnecting");
        this.disconnect();
        this.connect();
      } else {
        logger.error("WebSocket token refresh returned same token");
      }
    } catch (error) {
      logger.error("WebSocket token refresh failed:", error);
    }
  }

  connect(): void {
    if (this.isDestroyed || this.socket?.readyState === WebSocket.OPEN) {
      return;
    }

    this.intentionalClose = false;
    this.socket = new WebSocket(this.url);

    this.socket.onopen = () => {
      logger.info("WebSocket connected");
      this.reconnectAttempts = 0;
      this.consecutiveAuthFailures = 0;
      this.lastMessageAt = Date.now();

      const subscriptionPayload = {
        ...this.options,
        token: this.getCurrentToken(),
      };
      this.socket?.send(JSON.stringify(subscriptionPayload));

      this.scheduleHeartbeat();
      this.runtimeOptions.onOpen?.();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      this.lastMessageAt = Date.now();
      const data = JSON.parse(event.data);
      this.handleMessage(data);
    };

    this.socket.onerror = (error: Event) => {
      logger.error("WebSocket error:", error);
      this.errorListeners.forEach((listener) => listener(error));
    };

    this.socket.onclose = (event: CloseEvent) => {
      logger.info(
        `WebSocket closed (code: ${event.code}, clean: ${event.wasClean})`
      );
      this.clearTimers();
      this.runtimeOptions.onClose?.(event);

      if (!this.isDestroyed && !this.intentionalClose) {
        this.attemptReconnect();
      }
    };
  }

  private handleMessage(messageData: any): void {
    if (messageData.error) {
      const errorMessage = String(messageData.error).toLowerCase();
      if (
        errorMessage.includes("token") &&
        (errorMessage.includes("expired") || errorMessage.includes("invalid"))
      ) {
        logger.error("WebSocket token error:", messageData.error);
        this.handleTokenExpired();
        return;
      }
      logger.error("WebSocket server error:", messageData.error);
      return;
    }

    if (this.consecutiveAuthFailures > 0) {
      this.consecutiveAuthFailures = 0;
    }

    if (messageData.messageType === "chat-message") {
      this.chatListeners.forEach((listener) =>
        listener(messageData as ChatMessageResponse)
      );
      return;
    }

    if (messageData.messageType === "volume-creation") {
      this.volumeListeners.forEach((listener) =>
        listener(messageData as VolumeCreationResponse)
      );
      return;
    }

    const { data, updates } = messageData as EventMessageData;
    if (!data) {
      return;
    }
    if (
      data.subscribeTo === this.options.subscribeTo &&
      data.projectId === this.options.projectId
    ) {
      this.listeners.forEach((listener) => listener(updates));
    }
  }

  addListener(callback: ListenerCallback): void {
    this.listeners.push(callback);
  }

  removeListener(callback: ListenerCallback): void {
    const index = this.listeners.indexOf(callback);
    if (index !== -1) {
      this.listeners.splice(index, 1);
    }
  }

  addErrorListener(callback: ErrorListenerCallback): void {
    this.errorListeners.push(callback);
  }

  removeErrorListener(callback: ErrorListenerCallback): void {
    const index = this.errorListeners.indexOf(callback);
    if (index !== -1) {
      this.errorListeners.splice(index, 1);
    }
  }

  sendChatMessage(organizationId: string, message: string): void {
    if (!this.socket || !this.isConnected()) {
      throw new Error("WebSocket is not connected");
    }
    const chatMessage: ChatMessageRequest = {
      messageType: "chat-message",
      action: "send-message",
      chatId: this.options.chatId ?? "",
      projectId: this.options.projectId,
      organizationId,
      message,
      token: this.options.token,
    };

    this.socket.send(JSON.stringify(chatMessage));
  }

  addChatListener(callback: ChatMessageListenerCallback): void {
    this.chatListeners.push(callback);
  }

  removeChatListener(callback: ChatMessageListenerCallback): void {
    const index = this.chatListeners.indexOf(callback);
    if (index !== -1) {
      this.chatListeners.splice(index, 1);
    }
  }

  sendVolumeCreation(
    organizationId: string,
    sourceCaseId: string,
    name: string,
    documentIds: string[]
  ): void {
    if (!this.socket || !this.isConnected()) {
      throw new Error("WebSocket is not connected");
    }
    const volumeCreation: VolumeCreationRequest = {
      messageType: "volume-creation",
      projectId: sourceCaseId,
      organizationId,
      name,
      documentIds,
      token: this.options.token,
    };

    this.socket.send(JSON.stringify(volumeCreation));
  }

  addVolumeCreationListener(callback: VolumeCreationListenerCallback): void {
    this.volumeListeners.push(callback);
  }

  removeVolumeCreationListener(callback: VolumeCreationListenerCallback): void {
    const index = this.volumeListeners.indexOf(callback);
    if (index !== -1) {
      this.volumeListeners.splice(index, 1);
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.intentionalClose = true;
      this.socket.close();
      this.socket = null;
    }
  }

  destroy(): void {
    this.isDestroyed = true;
    this.intentionalClose = true;
    this.clearTimers();
    this.disconnect();
    this.listeners = [];
    this.errorListeners = [];
    this.chatListeners = [];
    this.volumeListeners = [];
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  sendMessage(message: Record<string, unknown>): void {
    if (!this.socket || !this.isConnected()) {
      throw new Error("WebSocket is not connected");
    }
    this.socket.send(JSON.stringify(message));
  }
}

export const mockWebSocketClient = {
  connect: () => {},
  disconnect: () => {},
  destroy: () => {},
  addListener: (_callback: ListenerCallback) => {},
  removeListener: (_callback: ListenerCallback) => {},
  addErrorListener: (_callback: ErrorListenerCallback) => {},
  removeErrorListener: (_callback: ErrorListenerCallback) => {},
  addChatListener: (_callback: ChatMessageListenerCallback) => {},
  removeChatListener: (_callback: ChatMessageListenerCallback) => {},
  sendChatMessage: (_organizationId: string, _message: string) => {},
  addVolumeCreationListener: (_callback: VolumeCreationListenerCallback) => {},
  removeVolumeCreationListener: (
    _callback: VolumeCreationListenerCallback
  ) => {},
  sendVolumeCreation: (
    _organizationId: string,
    _sourceCaseId: string,
    _name: string,
    _documentIds: string[]
  ) => {},
  isConnected: () => false,
};
